<template>
  <themed-modal
    name="manage-comps-modal"
    title="Manage Project Comps"
    size="xl"
    :scrollable="true"
    :clickToClose="!loading"
  >
    <div class="manage-comps-modal">
      <!-- Search Panel -->
      <div class="card">
        <themed-form
          :fields="searchFormFields"
          :model-value="searchFilters"
          @input="updateSearchFilters"
          layout="grid"
          :columns="4"
          :loading="loading"
        />
      </div>
      
      <!-- Action Buttons -->
      <div class="flex justify-end gap-4 my-5">
        <themed-button
          variant="default"
          size="default"
          @click="handleSearchProperties"
          :disabled="loading || !isSearchValid"
        >
          Search Properties
        </themed-button>
        
        <themed-button
          variant="outline"
          size="default"
          @click="handleResetFilters"
          :disabled="loading"
        >
          Reset Filters
        </themed-button>
      </div>


      <div v-if="hasSearched" class="header p-2">
          <h3 class="text-sm">
            Results
            <span>{{ filteredBuildings.length }} Properties</span>
          </h3>
        </div>
      <!-- Results Section -->
      <div class="card">
        <!-- Buildings Grid -->
        <themed-grid
          :data="filteredBuildings"
          :fields="gridFields"
          :actions="gridActions"
          :columns="3"
          :per-page="perPage"
          :pagination="true"
          :loading="loading"
          item-key="id"
          :empty-message="hasSearched ? 'No buildings found matching your search' : 'Use the search filters above to find properties'"
          empty-icon="home-icon"
          @card-click="handleBuildingClick"
          @action="handleGridAction"
        />
      </div>
    </div>
  </themed-modal>
</template>

<script>
import { mapState } from 'vuex';
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedGrid from '../ThemedGrid.vue';

export default {
  name: 'ManageCompsModal',
  
  components: {
    ThemedModal,
    ThemedButton,
    ThemedForm,
    ThemedGrid,
  },
  
  props: {
    projectId: {
      type: Number,
      required: true
    },
    compData: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      searchFilters: {
        city: '',
        state: '',
        zip: '',
        radius: '',
        propertyType: '',
        minSF: '',
        maxSF: '',
        sfPercentage: 10
      },
      loading: false,
      currentPage: 1,
      perPage: 8,
      projectCompIds: [],
      hasSearched: false,
      stateOptions: [],
      radiusOptions: [
        { label: '5 miles', value: '5' },
        { label: '10 miles', value: '10' },
        { label: '25 miles', value: '25' },
        { label: '50 miles', value: '50' },
        { label: '100 miles', value: '100' }
      ],
      propertyTypeOptions: []
    };
  },
  
  computed: {
    ...mapState(['buildings', 'projectComps']),
    
    allBuildings() {
      // Access buildings from Vuex store
      return this.buildings?.all || this.buildings?.filtered || [];
    },
    
    filteredBuildings() {
      return this.allBuildings.map(building => ({
        ...building,
        // Ensure we have a location field for subtitle
        location: `${building.city || ''}${building.city && building.state ? ', ' : ''}${building.state || ''}`,
        // Ensure we have a name field for title
        name: building.name || building.address_line_1 || 'Unknown Building'
      }));
    },
    
    isSearchValid() {
      // At least one search field must be filled (location OR property type OR size)
      // Handle both string values and potential object values
      const hasCity = !!(this.searchFilters.city && typeof this.searchFilters.city === 'string' && this.searchFilters.city.trim());
      const hasState = !!(this.searchFilters.state && (
        typeof this.searchFilters.state === 'string' ? this.searchFilters.state.trim() : this.searchFilters.state
      ));
      const hasZip = !!(this.searchFilters.zip && typeof this.searchFilters.zip === 'string' && this.searchFilters.zip.trim());
      const hasPropertyType = !!(this.searchFilters.propertyType);
      const hasMinSF = !!(this.searchFilters.minSF);
      const hasMaxSF = !!(this.searchFilters.maxSF);
      
      return hasCity || hasState || hasZip || hasPropertyType || hasMinSF || hasMaxSF;
    },
    
    gridFields() {
      return [
        // Image
        {
          field: 'image_url',
          type: 'image',
          defaultImage: '/img/temp/building-sample.jpg'
        },
        // Title
        {
          field: 'name',
          type: 'title'
        },
        // Subtitle
        {
          field: 'location',
          type: 'subtitle'
        },
        // Display fields
        {
          field: 'available_square_footage',
          label: 'AVAILABLE SQ. FT.',
          type: 'number'
        },
        {
          field: 'total_square_footage',
          label: 'TOTAL SQ. FT.',
          type: 'number'
        },
        {
          field: 'type.type',
          label: 'TYPE',
          type: 'text',
          transform: (value) => value || '-'
        }
      ];
    },
    
    gridActions() {
      return [
        {
          name: 'request',
          label: 'Request Details',
          icon: 'download',
          variant: 'default',
          visible: (item) => {
            // Show for imported buildings that:
            // 1. Haven't been scraped yet (no status)
            // 2. Failed scraping (allow retry)
            // 3. Haven't been converted to a building yet
            return item.is_imported && 
                   (!item.scraper_status || item.scraper_status === 'failed') && 
                   !item.building_id;
          },
          disabled: () => this.loading
        },
        {
          name: 'status',
          label: 'Check Status',
          icon: 'refresh',
          variant: 'outline',
          visible: (item) => {
            // Show for imported buildings that are queued or processing (not failed, not done)
            return item.is_imported && 
                   item.scraper_status && 
                   item.scraper_status !== 'done' && 
                   item.scraper_status !== 'failed' && 
                   !item.building_id;
          },
          disabled: () => this.loading
        },
        {
          name: 'add',
          label: 'Add to Project',
          icon: 'add',
          variant: 'default',
          visible: (item) => {
            // Show for regular buildings not in project
            if (!item.is_imported) {
              return !this.isInProject(item.id);
            }
            // Show for imported buildings that have been converted to regular buildings
            if (item.is_imported && item.building_id) {
              return !this.isInProject(item.building_id);
            }
            return false;
          },
          disabled: () => this.loading
        },
        {
          name: 'remove',
          label: 'Remove',
          icon: 'trash',
          variant: 'destructive',
          visible: (item) => {
            // Show for regular buildings in project
            if (!item.is_imported) {
              return this.isInProject(item.id);
            }
            // Show for imported buildings that have been converted and are in project
            if (item.is_imported && item.building_id) {
              return this.isInProject(item.building_id);
            }
            return false;
          },
          disabled: () => this.loading
        }
      ];
    },
    
    searchFormFields() {
      return [
        {
          name: 'city',
          type: 'input',
          label: 'City',
          col: 1
        },
        {
          name: 'state',
          type: 'select',
          label: 'State',
          options: this.stateOptions,
          col: 1
        },
        {
          name: 'zip',
          type: 'input',
          label: 'Zip',
          col: 1
        },
        {
          name: 'radius',
          type: 'select',
          label: 'Radius',
          options: this.radiusOptions,
          col: 1
        },
        {
          name: 'propertyType',
          type: 'select',
          label: 'Property Type',
          options: this.propertyTypeOptions,
          col: 1
        },
        {
          name: 'minSF',
          type: 'input',
          inputType: 'number',
          label: 'Min SF',
          col: 1
        },
        {
          name: 'maxSF',
          type: 'input',
          inputType: 'number',
          label: 'Max SF',
          col: 1
        },
        {
          name: 'sfPercentage',
          type: 'slider',
          label: '+/- SF %',
          col: 1,
          componentProps: {
            min: 0,
            max: 100,
            step: 5,
            showValue: true
          }
        }
      ];
    },
    
    tableColumns() {
      return [
        { field: 'address', label: 'Address', sortable: true },
        { field: 'leed_certification', label: 'LEED', sortable: true, align: 'center', width: '100px' },
        { field: 'class', label: 'Class', sortable: true, align: 'center', width: '100px' },
        { field: 'total_square_footage', label: 'Sq. Ft.', sortable: true, align: 'right', width: '120px' },
        { field: 'available_square_footage', label: 'Avail Sq. Ft.', sortable: true, align: 'right', width: '120px' },
        { field: 'floors', label: 'Floors', sortable: true, align: 'right', width: '100px' },
        { field: 'for_sale', label: 'For Sale', sortable: true, align: 'center', width: '100px' },
        { field: 'actions', label: '', sortable: false, width: '100px', align: 'right' }
      ];
    },
    
    tableData() {
      return this.filteredBuildings.map(building => ({
        ...building,
        // Format numbers for display
        total_square_footage: building.total_square_footage ? this.formatCompactNumber(building.total_square_footage) : '-',
        available_square_footage: building.available_square_footage ? this.formatCompactNumber(building.available_square_footage) : '-',
        floors: building.floors ? this.formatCompactNumber(building.floors) : '-',
        leed_certification: building.leed_certification || '-',
        class: building.class || '-'
      }));
    }
  },
  
  watch: {
    filteredBuildings() {
      // Data changed, ThemedGrid will handle page reset
    }
  },
  
  mounted() {
    // Initialize project comp IDs
    this.projectCompIds = this.compData.map(comp => comp.building_id || comp.id);
    
    // Initialize buildings store if not exists
    if (!this.buildings) {
      this.$store.state.buildings = {
        all: [],
        filtered: []
      };
    }
    
    // Load property types
    this.loadPropertyTypes();
    
    // Load states
    this.loadStates();
    
    // Listen for modal opened event
    this.$root.$on('modal::opened', (modalName) => {
      if (modalName === 'manage-comps-modal') {
        this.resetFilters();
      }
    });
  },
  
  beforeDestroy() {
    this.$root.$off('modal::opened');
  },
  
  methods: {
    updateSearchFilters(newFilters) {
      this.searchFilters = newFilters;
    },
    
    async loadPropertyTypes() {
      try {
        const response = await axios.get('/api/building-types');
        
        // Handle both array and object with types property
        const types = Array.isArray(response.data) 
          ? response.data 
          : (response.data.types || []);
        
        this.propertyTypeOptions = types.map(type => ({
          label: type.type || type.name || 'Unknown',
          value: String(type.id)
        }));
      } catch (error) {
        console.error('Error loading property types:', error);
        // Fallback to empty array
        this.propertyTypeOptions = [];
      }
    },
    
    async loadStates() {
      try {
        const response = await axios.get('/api/states');
        
        if (!response.data || !Array.isArray(response.data)) {
          console.error('Invalid states response format:', response.data);
          this.stateOptions = [];
          return;
        }
        
        this.stateOptions = response.data
          .map(state => state.state)
          .filter(abbr => abbr);
      } catch (error) {
        console.error('Error loading states:', error);
        this.stateOptions = [];
      }
    },
    
    async handleSearchProperties() {
      if (!this.isSearchValid) {
        this.$toast.warning('Please enter at least one search criteria (location, property type, or size)');
        return;
      }
      
      this.loading = true;
      this.hasSearched = true;
      
      try {
        const params = {};
        
        // Add location filters
        if (this.searchFilters.city) params.city = this.searchFilters.city;
        
        if (this.searchFilters.state) {
          params.state = typeof this.searchFilters.state === 'object' 
            ? (this.searchFilters.state.value || this.searchFilters.state)
            : this.searchFilters.state;
        }
        
        if (this.searchFilters.zip) params.zip = this.searchFilters.zip;
        if (this.searchFilters.radius) params.radius = this.searchFilters.radius;
        
        // Add property filters
        if (this.searchFilters.propertyType) params.building_type_id = this.searchFilters.propertyType;
        if (this.searchFilters.minSF) params.min_sf = this.searchFilters.minSF;
        if (this.searchFilters.maxSF) params.max_sf = this.searchFilters.maxSF;
        if (this.searchFilters.sfPercentage) params.sf_percentage = this.searchFilters.sfPercentage;
        
        const response = await axios.get('/api/buildings/search', { params });
        
        // Update Vuex store
        const buildingsData = response.data || [];
        
        if (!this.buildings) {
          this.$store.state.buildings = {
            all: buildingsData,
            filtered: buildingsData
          };
        } else {
          this.buildings.all = buildingsData;
          this.buildings.filtered = buildingsData;
        }
        
        if (buildingsData.length === 0) {
          this.$toast.info('No properties found matching your search criteria');
        }
      } catch (error) {
        console.error('Error searching buildings:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to search buildings');
      } finally {
        this.loading = false;
      }
    },
    
    handleResetFilters() {
      this.searchFilters = {
        city: '',
        state: '',
        zip: '',
        radius: '',
        propertyType: '',
        minSF: '',
        maxSF: '',
        sfPercentage: 10
      };
      
      this.hasSearched = false;
      
      // Clear buildings
      if (!this.buildings) {
        this.$store.state.buildings = {
          all: [],
          filtered: []
        };
      } else {
        this.buildings.all = [];
        this.buildings.filtered = [];
      }
      
      this.currentPage = 1;
    },
    
    handleBuildingClick(building) {
      // Optional: Navigate to building details or do nothing
      // this.$emit('building-click', building);
    },
    
    handleGridAction({ action, item }) {
      if (action === 'add') {
        this.addToProject(item);
      } else if (action === 'remove') {
        this.removeFromProject(item);
      } else if (action === 'request') {
        this.requestBuildingDetails(item);
      } else if (action === 'status') {
        this.checkScraperStatus(item);
      }
    },
    
    isInProject(buildingId) {
      // Handle both regular and imported building IDs
      if (typeof buildingId === 'string' && buildingId.startsWith('imported_')) {
        // For imported buildings that haven't been converted yet
        return false;
      }
      return this.projectCompIds.includes(buildingId);
    },
    
    async addToProject(building) {
      // Determine the actual building ID to use
      let buildingId = building.id;
      
      // If this is an imported building that has been converted, use the building_id
      if (building.is_imported && building.building_id) {
        buildingId = building.building_id;
      }
      
      // Check if it's an imported building that hasn't been converted yet
      if (building.is_imported && !building.building_id) {
        this.$toast.info('This building must be converted from imported data before adding to projects. Please request details first.');
        return;
      }
      
      this.loading = true;
      
      try {
        const response = await axios.post(`/projects/${this.projectId}/comps`, {
          building_id: buildingId
        });
        
        // Add to project comp IDs
        this.projectCompIds.push(buildingId);
        
        // Update projectComps Vuex store
        if (this.projectComps) {
          this.projectComps.all = response.data.comps;
          this.projectComps.filtered = response.data.comps;
        }
        
        // Emit event to parent with the comps from the response
        this.$emit('comp-added', response.data.comps);
        
        this.$toast.success(response.data.message || 'Building added to project');
      } catch (error) {
        console.error('Error adding comp:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to add building to project');
      } finally {
        this.loading = false;
      }
    },
    
    viewBuildingDetails(building) {
      // Navigate to imported building details page using the link field
      if (building.is_imported && building.link) {
        // Open the link in a new tab
        window.open(building.link, '_blank');
      } else if (building.is_imported) {
        // Fallback notification if link is not available
        this.$toast.warning('Building details link is not available for this imported building.');
      }
    },
    
    async requestBuildingDetails(building) {
      if (!building.is_imported) {
        return;
      }
      
      this.loading = true;
      
      try {
        // Extract the imported building ID from the composite ID
        const importedBuildingId = building.id.toString().replace('imported_', '');
        
        // If this is a retry, show appropriate message
        const isRetry = building.scraper_status === 'failed';
        
        await axios.post(`/api/buildings/scraper/${importedBuildingId}/request`, {
          project_id: this.projectId // Pass project context for auto-add
        });
        
        if (isRetry) {
          this.$toast.success('Scraper request has been requeued. The building will be automatically added to your project once ready.');
        } else {
          this.$toast.success('Scraper request has been queued. The building will be automatically added to your project once ready.');
        }
        
        // Clear any previous error and update status
        building.scraper_status = 'queued';
        building.scraper_error = null;
        
        // Optionally, start polling for status
        this.pollScraperStatus(building, importedBuildingId);
        
      } catch (error) {
        console.error('Error requesting building details:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to request building details');
      } finally {
        this.loading = false;
      }
    },
    
    async checkScraperStatus(building) {
      if (!building.is_imported) {
        return;
      }
      
      this.loading = true;
      
      try {
        const importedBuildingId = building.id.toString().replace('imported_', '');
        
        const response = await axios.get(`/api/buildings/scraper/${importedBuildingId}/status`);
        
        const status = response.data.status;
        
        // Update local building data
        building.scraper_status = status;
        building.building_id = response.data.building_id;
        
        if (status === 'done') {
          this.$toast.success('Building has been created successfully and added to your project!');
          
          // Add to project comp IDs
          if (building.building_id) {
            this.projectCompIds.push(building.building_id);
          }
          
          // Reload project comps from server
          await this.reloadProjectComps();
        } else if (status === 'failed') {
          this.$toast.error(response.data.error || 'Failed to scrape building details');
        } else {
          this.$toast.info(`Current status: ${status}. Please check again in a moment.`);
        }
        
      } catch (error) {
        console.error('Error checking scraper status:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to check scraper status');
      } finally {
        this.loading = false;
      }
    },
    
    pollScraperStatus(building, importedBuildingId, attempts = 0) {
      const maxAttempts = 60; // Poll for up to 10 minutes (10s intervals)
      
      if (attempts >= maxAttempts) {
        return;
      }
      
      setTimeout(async () => {
        try {
          const response = await axios.get(`/api/buildings/scraper/${importedBuildingId}/status`);
          
          const status = response.data.status;
          building.scraper_status = status;
          building.building_id = response.data.building_id;
          
          if (status === 'done') {
            this.$toast.success('The building has been successfully created and added to your project!');
            
            // Add to project comp IDs for UI update
            if (building.building_id) {
              this.projectCompIds.push(building.building_id);
            }
            
            // Reload project comps from server
            await this.reloadProjectComps();
          } else if (status === 'failed') {
            this.$toast.error(response.data.error || 'Failed to scrape building details');
          } else {
            // Continue polling
            this.pollScraperStatus(building, importedBuildingId, attempts + 1);
          }
        } catch (error) {
          console.error('Error polling scraper status:', error);
        }
      }, 10000); // Poll every 10 seconds
    },
    
    viewCreatedBuilding(building) {
      if (building.building_id) {
        // Navigate to the created building
        window.open(`/buildings/${building.building_id}`, '_blank');
      } else {
        this.$toast.warning('Building has not been created yet.');
      }
    },
    
    async removeFromProject(building) {
      this.loading = true;
      
      try {
        // Determine the actual building ID
        let buildingId = building.id;
        if (building.is_imported && building.building_id) {
          buildingId = building.building_id;
        }
        
        // Find the comp ID (pivot ID) for this building
        const comp = this.compData.find(c => (c.building_id || c.id) === buildingId);
        const compId = comp?.pivot?.id || comp?.id;
        
        const response = await axios.delete(`/projects/${this.projectId}/comps/${compId}`);
        
        // Remove from project comp IDs
        this.projectCompIds = this.projectCompIds.filter(id => id !== buildingId);
        
        // Update projectComps Vuex store
        if (this.projectComps) {
          this.projectComps.all = response.data.comps;
          this.projectComps.filtered = response.data.comps;
        }
        
        // Emit event to parent to update project data
        this.$emit('comp-removed', response.data.comps);
        
        this.$toast.success(response.data.message || 'Building removed from project');
      } catch (error) {
        console.error('Error removing comp:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to remove building from project');
      } finally {
        this.loading = false;
      }
    },
    
    formatNumber(value) {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat('en-US').format(value);
    },
    
    formatCompactNumber(value) {
      if (!value && value !== 0) return '-';
      
      const num = parseFloat(value);
      
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      
      return num.toString();
    },
    
    async reloadProjectComps() {
      try {
        console.log('Reloading project comps for project:', this.projectId);
        const response = await axios.get(`/projects/${this.projectId}`);
        
        console.log('Project data received:', response.data);
        
        if (response.data && response.data.comps) {
          console.log('Emitting comp-added with comps:', response.data.comps);
          // Emit the updated comps to parent
          this.$emit('comp-added', response.data.comps);
        } else {
          console.warn('No comps data in response');
          this.$emit('comp-added', []);
        }
      } catch (error) {
        console.error('Error reloading project comps:', error);
        // Still emit event to trigger parent refresh
        this.$emit('comp-added', []);
      }
    },
    
    closeModal() {
      if (!this.loading) {
        this.$modal.hide('manage-comps-modal');
      }
    }
  }
};
</script>
